import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";

const ViewVendorSegment = () => {
    const navigate = useNavigate();
    const { segmentName } = useParams<{ segmentName: string }>();

    const [searchTerm, setSearchTerm] = useState("");

    // Segment data (same as in manage-vendors/index.tsx)
    const segmentData = [
        // Revenue & Growth Segments
        {
            title: "High-Value Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with lifetime value > AED 5000",
            count: 850,
            status: "Active",
            icon: "💎",
            useCase: "Eligible for reduced commission",
            purpose: "Incentives",
            isPredictive: false,
        },
        {
            title: "Low Conversion Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with conversion rate < 2%",
            count: 1240,
            status: "Active",
            icon: "📉",
            useCase: "Used for incentives",
            purpose: "Incentives",
            isPredictive: false,
        },
        {
            title: "High Cart Drop Rate Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with cart abandonment > 60%",
            count: 980,
            status: "Active",
            icon: "🛒",
            useCase: "Used for incentives",
            purpose: "Incentives",
            isPredictive: false,
        },
        {
            title: "Low Sell-Through Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with sell-through rate < 30%",
            count: 1560,
            status: "Active",
            icon: "📊",
            useCase: "Used for incentives",
            purpose: "Incentives",
            isPredictive: false,
        },
        {
            title: "Low Checkout Completion Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with checkout completion < 40%",
            count: 1120,
            status: "Active",
            icon: "💳",
            useCase: "Used for incentives",
            purpose: "Incentives",
            isPredictive: false,
        },
        {
            title: "Fast-growing Vendors (30d)",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with >50% growth in last 30 days",
            count: 320,
            status: "Active",
            icon: "🚀",
            useCase: "Feed pricing, commission, and promo strategy",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "Fast-growing Vendors (90d)",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with >50% growth in last 90 days",
            count: 450,
            status: "Active",
            icon: "📈",
            useCase: "Feed pricing, commission, and promo strategy",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "Declining Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with declining orders > 20%",
            count: 680,
            status: "Active",
            icon: "📉",
            useCase: "Feed pricing, commission, and promo strategy",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "High GMV, Low Margin Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with GMV > AED 100K, margin < 15%",
            count: 240,
            status: "Active",
            icon: "💰",
            useCase: "Feed pricing, commission, and promo strategy",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "Low GMV, High Margin Vendors",
            type: "Dynamic",
            category: "Revenue & Growth",
            description: "Vendors with GMV < AED 50K, margin > 30%",
            count: 890,
            status: "Active",
            icon: "💎",
            useCase: "Feed pricing, commission, and promo strategy",
            purpose: "Analytics",
            isPredictive: false,
        },
        // Risk & Compliance Segments (CRITICAL)
        {
            title: "Vendors with Policy Violations",
            type: "Dynamic",
            category: "Risk & Compliance",
            description: "Vendors with active policy violations",
            count: 45,
            status: "Active",
            icon: "🚨",
            useCase: "Used for risk monitoring",
            purpose: "Risk Monitoring",
            isPredictive: false,
        },
        {
            title: "Vendors with High Refund Rates",
            type: "Dynamic",
            category: "Risk & Compliance",
            description: "Vendors with refund rate > 10%",
            count: 120,
            status: "Active",
            icon: "↩️",
            useCase: "Used for risk monitoring",
            purpose: "Risk Monitoring",
            isPredictive: false,
        },
        {
            title: "Vendors with Counterfeit Flags",
            type: "Dynamic",
            category: "Risk & Compliance",
            description: "Vendors flagged for counterfeit products",
            count: 8,
            status: "Active",
            icon: "⚠️",
            useCase: "Used for risk monitoring",
            purpose: "Risk Monitoring",
            isPredictive: false,
        },
        {
            title: "Vendors with Delayed Shipments",
            type: "Dynamic",
            category: "Risk & Compliance",
            description: "Vendors with >15% delayed shipments",
            count: 180,
            status: "Active",
            icon: "⏰",
            useCase: "Used for risk monitoring",
            purpose: "Risk Monitoring",
            isPredictive: false,
        },
        // Operational Quality Segments
        {
            title: "Low Fulfillment SLA Vendors",
            type: "Dynamic",
            category: "Operational Quality",
            description: "Vendors with SLA breach > 5%",
            count: 340,
            status: "Active",
            icon: "📦",
            useCase: "Connect directly to Vendor Lifecycle",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "High Cancellation Vendors",
            type: "Dynamic",
            category: "Operational Quality",
            description: "Vendors with cancellation rate > 8%",
            count: 210,
            status: "Active",
            icon: "❌",
            useCase: "Connect directly to Vendor Lifecycle",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "Low Rating Vendors",
            type: "Dynamic",
            category: "Operational Quality",
            description: "Vendors with rating < 3.5",
            count: 95,
            status: "Active",
            icon: "⭐",
            useCase: "Connect directly to Vendor Lifecycle",
            purpose: "Analytics",
            isPredictive: false,
        },
        {
            title: "High Dispute Vendors",
            type: "Dynamic",
            category: "Operational Quality",
            description: "Vendors with dispute rate > 5%",
            count: 150,
            status: "Active",
            icon: "⚖️",
            useCase: "Connect directly to Vendor Lifecycle",
            purpose: "Analytics",
            isPredictive: false,
        },
        // Static Segments
        {
            title: "UAE Vendors",
            type: "Static",
            category: "Geographic",
            description: "Vendors based in UAE",
            count: 12500,
            status: "Active",
            icon: "🇦🇪",
            useCase: "Used for communication",
            purpose: "Communication",
            isPredictive: false,
        },
        {
            title: "Fashion Vendors",
            type: "Static",
            category: "Category",
            description: "Fashion category vendors",
            count: 5600,
            status: "Active",
            icon: "👗",
            useCase: "Used for communication",
            purpose: "Communication",
            isPredictive: false,
        },
        {
            title: "New Signups",
            type: "Dynamic",
            category: "Onboarding",
            description: "Recently registered vendors",
            count: 890,
            status: "Active",
            icon: "✨",
            useCase: "Used for communication",
            purpose: "Communication",
            isPredictive: false,
        },
        // Predictive Segments
        {
            title: "Lapsed Vendors",
            type: "Predictive",
            category: "Predictive",
            description: "Inactive vendors",
            count: 2300,
            status: "Active",
            icon: "⚠️",
            useCase: "Used for risk monitoring",
            purpose: "Risk Monitoring",
            isPredictive: true,
            aiReason: "Declining orders, inactivity",
            confidence: "High",
            suggestedAction: "Send re-engagement campaign",
        },
    ];

    // AI Suggested Segments
    const aiSuggestedSegments = [
        {
            title: "Vendors likely to churn in 30 days",
            type: "Predictive",
            category: "AI Suggested",
            description: "AI-predicted churn risk based on activity patterns",
            count: 145,
            status: "Active",
            icon: "🔮",
            useCase: "Used for risk monitoring",
            purpose: "Risk Monitoring",
            isPredictive: true,
            aiReason: "Declining orders, reduced activity, low engagement",
            confidence: "High",
            suggestedAction: "Apply retention incentives, assign account manager",
        },
        {
            title: "Vendors ready for tier upgrade",
            type: "Predictive",
            category: "AI Suggested",
            description: "Vendors meeting criteria for next subscription tier",
            count: 78,
            status: "Active",
            icon: "⬆️",
            useCase: "Used for incentives",
            purpose: "Incentives",
            isPredictive: true,
            aiReason: "High growth rate, consistent performance, revenue threshold met",
            confidence: "Medium",
            suggestedAction: "Send upgrade offer, schedule consultation",
        },
        {
            title: "Vendors suitable for co-marketing",
            type: "Predictive",
            category: "AI Suggested",
            description: "Vendors with high engagement and brand alignment",
            count: 92,
            status: "Active",
            icon: "🤝",
            useCase: "Used for communication",
            purpose: "Communication",
            isPredictive: true,
            aiReason: "High customer satisfaction, strong brand presence, active engagement",
            confidence: "Medium",
            suggestedAction: "Reach out for co-marketing opportunities",
        },
    ];

    // Combine all segments
    const allSegments = [...segmentData, ...aiSuggestedSegments];

    // Find the current segment
    const currentSegment = allSegments.find(
        (segment) =>
            segment.title.toLowerCase().replace(/\s+/g, "-") ===
            segmentName?.toLowerCase().replace(/\s+/g, "-")
    );

    // Sample vendor data
    const vendorData = [
        {
            id: "VND-001",
            name: "Fashion House Dubai",
            email: "contact@fashionhouse.ae",
            phone: "+971501234567",
            type: "Fashion",
            status: "Active",
            health: 92.5,
            tier: "Enterprise",
            revenue: "AED 1,250,000",
            stores: "6",
            lastOrder: "05-01-2026",
            orders: 1250,
        },
        {
            id: "VND-002",
            name: "TechGadgets UAE",
            email: "info@techgadgets.ae",
            phone: "+971502345678",
            type: "Electronics",
            status: "Active",
            health: 78.3,
            tier: "Pro",
            revenue: "AED 456,000",
            stores: "1",
            lastOrder: "02-01-2026",
            orders: 890,
        },
        {
            id: "VND-003",
            name: "Beauty Essentials",
            email: "info@beautyessentials.ae",
            phone: "+971503456789",
            type: "Beauty",
            status: "Active",
            health: 85.2,
            tier: "Standard",
            revenue: "AED 320,000",
            stores: "3",
            lastOrder: "01-15-2026",
            orders: 650,
        },
        {
            id: "VND-004",
            name: "Home Decor Plus",
            email: "contact@homedecor.ae",
            phone: "+971504567890",
            type: "Home",
            status: "At Risk",
            health: 65.8,
            tier: "Standard",
            revenue: "AED 180,000",
            stores: "2",
            lastOrder: "12-20-2025",
            orders: 320,
        },
        {
            id: "VND-005",
            name: "Sports Zone",
            email: "info@sportszone.ae",
            phone: "+971505678901",
            type: "Sports",
            status: "Active",
            health: 88.7,
            tier: "Pro",
            revenue: "AED 580,000",
            stores: "4",
            lastOrder: "01-10-2026",
            orders: 1120,
        },
    ];

    // For segments, display all vendors or filter based on segment logic
    const segmentVendors = vendorData; // Or filter based on segment logic if available

    const vendorColumns = [
        "VENDOR",
        "CONTACT",
        "CATEGORY",
        "STATUS",
        "HEALTH SCORE",
        "TIER",
        "REVENUE",
        "ORDERS",
        "LAST ORDER",
        "ACTIONS",
    ];

    // Filter vendors based on search term
    const filteredVendors = segmentVendors.filter(
        (vendor) =>
            vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className="main_title">
                <h1>View Segment: {currentSegment?.title || "Unknown Segment"}</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/manage-vendors")}>
                        Manage Vendors
                    </button>{" "}
                    - View Segment
                </p>
            </div>

            {/* Segment Details */}
            <div className="cards">
                <div className="gap_p view_page">
                    <div className="w_33">
                        <h6>Segment Title</h6>
                        <p>
                            {currentSegment?.icon} {currentSegment?.title}
                        </p>
                    </div>
                    <div className="w_33">
                        <h6>Type</h6>
                        <p>
                            <span
                                className={`badge1 ${currentSegment?.type.toLowerCase()}`}
                            >
                                {currentSegment?.type}
                            </span>
                        </p>
                    </div>
                    {/* <div className="w_33">
                        <h6>Category</h6>
                        <p>{currentSegment?.category || "—"}</p>
                    </div> */}
                    <div className="w_33">
                        <h6>Vendor Count</h6>
                        <p>{currentSegment?.count?.toLocaleString() || 0}</p>
                    </div>
                    <div className="w_33">
                        <h6>Status</h6>
                        <p>
                            <span className={`badge1 ${currentSegment?.status.toLowerCase()}`}>
                                {currentSegment?.status}
                            </span>
                        </p>
                    </div>
                    <div className="w_33">
                        <h6>Purpose</h6>
                        <p>{currentSegment?.purpose || "—"}</p>
                    </div>
                    <div className="w_100">
                        <h6>Description</h6>
                        <p>{currentSegment?.description || "—"}</p>
                    </div>
                    <div className="w_100">
                        <h6>Primary Use Case</h6>
                        <p>{currentSegment?.useCase || "—"}</p>
                    </div>
                    {currentSegment?.isPredictive && (
                        <>
                            <div className="w_33">
                                <h6>Prediction Reason</h6>
                                <p>{currentSegment?.aiReason || "—"}</p>
                            </div>
                            <div className="w_33">
                                <h6>Confidence Level</h6>
                                <p>
                                    <span
                                        className={`badge1 ${(currentSegment?.confidence || "medium").toLowerCase()}`}
                                    >
                                        {currentSegment?.confidence || "Medium"}
                                    </span>
                                </p>
                            </div>
                            <div className="w_33">
                                <h6>Suggested Action</h6>
                                <p>{currentSegment?.suggestedAction || "—"}</p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Vendors Table */}
            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <Box className="search_box">
                            <SearchBar
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setSearchTerm}
                                placeholder="Search vendors..."
                            />
                        </Box>
                    </div>
                    <div className="right_s">
                        <p>Total Vendors: {filteredVendors.length}</p>
                    </div>
                </div>

                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                {vendorColumns.map((column, index) => (
                                    <TableCell key={index}>{column}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredVendors.map((vendor) => (
                                <TableRow key={vendor.id} hover>
                                    <TableCell>
                                        <div className="user_block">
                                            <p>
                                                <b>{vendor.name}</b>
                                                <span>{vendor.id}</span>
                                            </p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <p>{vendor.email}</p>
                                        <span className="sub_text">{vendor.phone}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="badge1 info">{vendor.type}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={`badge1 ${vendor.status.toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            {vendor.status}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="health_score">
                                            <span>{vendor.health}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="badge1">{vendor.tier}</span>
                                    </TableCell>
                                    <TableCell>{vendor.revenue}</TableCell>
                                    <TableCell>{vendor.orders.toLocaleString()}</TableCell>
                                    <TableCell>{vendor.lastOrder}</TableCell>
                                    <TableCell>
                                        <div className="table_actions">
                                            <IconButton
                                                onClick={() =>
                                                    navigate("/manage-vendors/view-vendor")
                                                }
                                                size="small"
                                            >
                                                <img
                                                    src="/static/images/eye_icon.svg"
                                                    alt="View"
                                                />
                                            </IconButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </>
    );
};

export default ViewVendorSegment;
